"use client";

import { useState, useEffect, useCallback } from "react";

const STORAGE_KEY = "recentSearches";
const MAX_ITEMS = 15;

/**
 * localStorage 저장 포맷.
 * - `label`: 화면에 표시할 검색어(예: "일본 갤럭시")
 * - `href`: 검색 실행 시의 목적지 경로(locale 미포함, 휘발성 `t=` 제외).
 *   지역(`delivery_country`/bounds)·키워드를 그대로 담아 클릭 시 동일 검색을 재현한다.
 *   구버전 항목은 href가 없으므로(null) 클릭 시 label로 재검색하는 폴백을 쓴다.
 */
interface RecentSearchItem {
  label: string;
  href: string | null;
  searchedAt: number;
}

export interface RecentSearchView {
  label: string;
  href: string | null;
}

/**
 * 구버전 포맷({ query, searchedAt }) → 신버전({ label, href, searchedAt }) 마이그레이션.
 * 알 수 없는 형태는 버린다.
 */
function normalizeStored(raw: unknown): RecentSearchItem[] {
  if (!Array.isArray(raw)) return [];
  const items: RecentSearchItem[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const e = entry as Record<string, unknown>;
    const label =
      typeof e.label === "string"
        ? e.label
        : typeof e.query === "string"
          ? e.query
          : null;
    if (!label) continue;
    items.push({
      label,
      href: typeof e.href === "string" ? e.href : null,
      searchedAt: typeof e.searchedAt === "number" ? e.searchedAt : 0,
    });
  }
  return items;
}

/**
 * 최근 검색어를 localStorage에 저장하고 관리하는 훅
 * 최대 15개까지 저장, 오래된 것부터 삭제
 */
export function useRecentSearches() {
  const [recentSearches, setRecentSearches] = useState<RecentSearchView[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  // 초기 로드
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const items = normalizeStored(JSON.parse(stored)).sort(
          (a, b) => b.searchedAt - a.searchedAt,
        );
        setRecentSearches(items.map(({ label, href }) => ({ label, href })));
      }
    } catch (error) {
      console.error("Failed to load recent searches:", error);
    }
    setIsLoaded(true);
  }, []);

  // 검색어 추가 (label = 표시 문자열, href = 재현용 목적지 경로)
  const addRecentSearch = useCallback(
    (label: string, href: string | null = null) => {
      const trimmed = label.trim();
      if (!trimmed) return;

      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        let items = normalizeStored(stored ? JSON.parse(stored) : []);

        // 동일 검색어 제거 (최신으로 갱신)
        items = items.filter((item) => item.label !== trimmed);

        // 맨 앞에 추가
        items.unshift({ label: trimmed, href, searchedAt: Date.now() });

        // 최대 개수 초과 시 삭제
        if (items.length > MAX_ITEMS) {
          items = items.slice(0, MAX_ITEMS);
        }

        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
        setRecentSearches(items.map(({ label, href }) => ({ label, href })));
      } catch (error) {
        console.error("Failed to add recent search:", error);
      }
    },
    [],
  );

  // 개별 삭제 (label 기준)
  const removeRecentSearch = useCallback((label: string) => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      let items = normalizeStored(stored ? JSON.parse(stored) : []);

      items = items.filter((item) => item.label !== label);

      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      setRecentSearches(items.map(({ label, href }) => ({ label, href })));
    } catch (error) {
      console.error("Failed to remove recent search:", error);
    }
  }, []);

  // 전체 삭제
  const clearRecentSearches = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      setRecentSearches([]);
    } catch (error) {
      console.error("Failed to clear recent searches:", error);
    }
  }, []);

  return {
    recentSearches,
    addRecentSearch,
    removeRecentSearch,
    clearRecentSearches,
    isLoaded,
  };
}
